'use client';

import { useEffect, useRef } from 'react';
import { Box, Typography, CssBaseline, ThemeProvider } from '@mui/material';
import type { BackendPolyDataResponse } from '../types/vtk';
import { getFieldLabel } from '../types/vtk';
import { loadVtkModules } from '../lib/loadVtkModules';
import { createVtkScene, type VtkScene } from '../lib/createVtkScene';
import { alpha } from '@mui/material/styles';
import { createThemeFromTokens } from '@fast-computing/fast-graphics/mui-theme';

const selectedTheme = createThemeFromTokens('atlas', {
  withComponentDefaults: true,
});

type Props = {
  data: BackendPolyDataResponse | null;
  loading: boolean;
};

type ColorStop = {
  position: number;
  color: [number, number, number];
};

function hexToVtkColor(hex: string): [number, number, number] {
  const cleanHex = hex.replace('#', '');

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  return [r, g, b];
}

const MODEL_COLOR_STOPS: ColorStop[] = [
  { position: 0, color: hexToVtkColor(selectedTheme.palette.primary.main) },
  { position: 0.5, color: hexToVtkColor('#43eb34') },
  { position: 1, color: hexToVtkColor(selectedTheme.palette.error.main) },
];

const LEGEND_FONT_FAMILY = selectedTheme.typography.fontFamily;

function applyLookupTablePreset(
  lut: any,
  scalarRange: [number, number],
  colorStops: ColorStop[] = MODEL_COLOR_STOPS,
) {
  const [min, max] = scalarRange;
  const span = max - min;

  lut.removeAllPoints();

  colorStops.forEach(({ position, color }) => {
    const scalarValue = span === 0 ? min : min + span * position;
    lut.addRGBPoint(scalarValue, color[0], color[1], color[2]);
  });
}

function getFieldDescription(field?: string): string {
  if (field === 'agem1') {
    return 'Age M1 (seconds) is mathematically the first moment of the blood-age distribution, meaning the mean blood age accumulated up to that time. It directly quantifies stasis: when Age M1 is higher in a specific region, blood in that area is expected to be more stagnant.';
  }

  if (field === 'agem2') {
    return 'Age M2 (seconds²) is mathematically the second moment of the blood-age distribution. Unlike Age M1, it is not a direct physical marker, but it refines stasis analysis by measuring variability and dispersion of blood age in that region. If Age M1 gives the average age, Age M2 helps determine whether that average comes from steady flow or from a mix of very young and very old blood; when both are high, stasis interpretation is stronger.';
  }

  if (field === 'washout') {
    return "Washout (adimensional) describes the atrium's ability to clear old blood. It is the residual fraction of old blood that remains in a region after a number of cardiac cycles. Areas with high Washout indicate blood trapping, meaning old blood is not effectively replaced by new blood and confirming zones of high stasis.";
  }

  return '';
}

async function applyDataToScene(
  scene: VtkScene,
  data: BackendPolyDataResponse,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  // Set points
  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(data.points.flat()), 3);
  (scene.polydata as any).setPoints(points);

  // Set cells
  const flatCells: number[] = [];
  for (const cell of data.cells.connectivity) {
    flatCells.push(cell.length, ...cell);
  }
  const cellArray = (vtk.vtkCellArray as any).newInstance({
    values: Uint32Array.from(flatCells),
  });
  (scene.polydata as any).setPolys(cellArray);

  // Remove old scalar bar if it exists
  if (scene.scalarBarActor) {
    (scene.renderer as any).removeActor2D(scene.scalarBarActor);
    (scene.scalarBarActor as any).delete?.();
    scene.scalarBarActor = null;
  }

  // Set scalar data
  const scalarArrayName = data.field;
  const scalarData = data.point_data[scalarArrayName];

  if (scalarData && Array.isArray(scalarData.values)) {
    const scalarValues = scalarData.values as number[];
    const scalars = (vtk.vtkDataArray as any).newInstance({
      name: scalarArrayName,
      values: Float32Array.from(scalarValues),
      numberOfComponents: 1,
    });
    (scene.polydata as any).getPointData().setScalars(scalars);

    (scene.mapper as any).setScalarVisibility(true);
    (scene.mapper as any).setColorModeToMapScalars();
    (scene.mapper as any).setScalarModeToUsePointData();

    // Set up color transfer function
    const lut = (vtk.vtkColorTransferFunction as any).newInstance();
    const scalarRange = (scene.polydata as any)
      .getPointData()
      .getScalars()
      .getRange();

    applyLookupTablePreset(lut, scalarRange as [number, number]);

    (scene.mapper as any).setLookupTable(lut);
    (scene.mapper as any).setScalarRange(scalarRange);

    // Add scalar bar with field label
    const scalarBarActor = (vtk.vtkScalarBarActor as any).newInstance();
    scalarBarActor.setScalarsToColors(lut);
    scalarBarActor.setAxisTextStyle({
      fontFamily: LEGEND_FONT_FAMILY,
      fontColor: selectedTheme.palette.text.primary,
      fontStyle: 'normal',
    });
    scalarBarActor.setTickTextStyle({
      fontFamily: LEGEND_FONT_FAMILY,
      fontColor: selectedTheme.palette.text.primary,
      fontStyle: 'normal',
    });

    // Set the title to the field label
    const fieldLabel = getFieldLabel(data.field as any);
    scalarBarActor.setAxisLabel(fieldLabel);

    (scene.renderer as any).addActor2D(scalarBarActor);
    scene.scalarBarActor = scalarBarActor;
  } else {
    (scene.polydata as any).getPointData().setScalars(null);
    (scene.mapper as any).setScalarVisibility(false);
  }

  (scene.polydata as any).modified();
  (scene.mapper as any).modified();
  (scene.renderer as any).resetCamera();
  (scene.renderer as any).resetCameraClippingRange();
  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export default function VtkViewer({ data, loading }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<VtkScene | null>(null);
  const pipelineReady = useRef<Promise<void>>(Promise.resolve());
  const fieldDescription = getFieldDescription(data?.field);

  // Initialize VTK scene once
  useEffect(() => {
    let mounted = true;
    let resolveReady!: () => void;
    pipelineReady.current = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });

    let resizeObserver: ResizeObserver | null = null;

    async function init() {
      const container = containerRef.current;
      if (!container) {
        resolveReady();
        return;
      }

      await loadVtkModules();
      if (!mounted) {
        resolveReady();
        return;
      }

      const scene = createVtkScene(await loadVtkModules(), container);
      sceneRef.current = scene;

      resizeObserver = new ResizeObserver(() => {
        scene.resize();
      });
      resizeObserver.observe(container);

      resolveReady();
    }

    init().catch((err) => {
      console.error('VtkViewer init error:', err);
      resolveReady();
    });

    return () => {
      mounted = false;
      resizeObserver?.disconnect();
      sceneRef.current?.destroy();
      sceneRef.current = null;
      resolveReady();
    };
  }, []);

  // Update scene when data changes
  useEffect(() => {
    if (!data) return;

    async function update() {
      await pipelineReady.current;
      if (!sceneRef.current) return;

      await applyDataToScene(sceneRef.current, data);
    }

    void update();
  }, [data]);

  return (
    <ThemeProvider theme={selectedTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: `${selectedTheme.palette.background.default}`,
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!data && !loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backgroundColor: `${selectedTheme.palette.background.default}`,
            }}
          >
            <Typography
              variant='h6'
              color={`${selectedTheme.palette.text.secondary}`}
            >
              Loading...
            </Typography>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backgroundColor: `${selectedTheme.palette.background.default}`,
            }}
          >
            <Typography
              variant='h6'
              color={`${selectedTheme.palette.text.secondary}`}
            >
              Loading...
            </Typography>
          </Box>
        )}

        {fieldDescription && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              maxWidth: 560,
              zIndex: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant='body2'
              color={`${selectedTheme.palette.text.primary}`}
              fontFamily={`${selectedTheme.typography.fontFamily}`}
            >
              {fieldDescription}
            </Typography>
          </Box>
        )}

        <div
          ref={containerRef}
          style={{
            flex: 1,
            width: '100%',
            height: '100%',
            minHeight: 0,
            background: `${selectedTheme.palette.background.default}`,
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
